'use strict';
/**
 * uiStore — the React side of the one unified translation system.
 *
 * Fetches the owner-managed platform UI strings from GET /api/i18n/:lang (the
 * same source the SSR pages use) and overlays them on top of the in-code base
 * catalogues used by every translator (tu / ta / bt / T). Base text stays in
 * code as the default; this store is the single place owner edits and added
 * languages flow from, so all four surfaces share one system.
 *
 * Inert for any key the store doesn't have yet — translators just fall back to
 * their built-in default — so wiring it in can never break a working screen.
 */
import { useEffect, useState } from 'react';

const EMPTY = {};
const cache = {};     // lang -> { key: value }
const inflight = {};  // lang -> Promise
const subs = new Set();

export function peek(lang) { return (lang && cache[lang]) || EMPTY; }

export function load(lang) {
  if (!lang) return Promise.resolve(EMPTY);
  if (cache[lang]) return Promise.resolve(cache[lang]);
  if (inflight[lang]) return inflight[lang];
  const p = fetch('/api/i18n/' + encodeURIComponent(lang))
    .then(r => (r.ok ? r.json() : { strings: {} }))
    .then(d => { cache[lang] = d.strings || {}; subs.forEach(fn => fn()); return cache[lang]; })
    .catch(() => { cache[lang] = {}; return cache[lang]; })
    .finally(() => { delete inflight[lang]; });
  inflight[lang] = p;
  return p;
}

export function subscribe(fn) { subs.add(fn); return () => subs.delete(fn); }

/** Hook: returns the unified strings for `lang`, re-rendering when they load. */
export function useUnifiedUi(lang) {
  const [, bump] = useState(0);
  useEffect(() => {
    const unsub = subscribe(() => bump(n => n + 1));
    load(lang);
    return unsub;
  }, [lang]);
  return peek(lang);
}
